import { useState, useMemo } from 'react';
import {
  useGlobalVariable,
  useUpdateGlobalVariable
} from '@api/enterprise/globalVariable';
import { useCalibrationEventTypes } from '@api/enterprise/eventType';

// Default selected events
const DEFAULT_SELECTED_EVENTS = ['CALIBRATION'];

export const useAllowedEventsData = () => {
  const [localSelectedEvent, setLocalSelectedEvent] = useState<string | null>(
    null
  );
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Fetch calibration event types from API
  const {
    data: availableEventTypes = [],
    isLoading: isLoadingEventTypes,
    error: eventTypesError
  } = useCalibrationEventTypes();

  // Fetch current global variable
  const {
    data: globalVariableData,
    isLoading: isLoadingGlobalVariable,
    error: globalVariableError,
    refetch
  } = useGlobalVariable('HIDE_ALLOWED_EVENT_TYPES', true);

  // Update global variable mutation
  const updateGlobalVariableMutation = useUpdateGlobalVariable();

  // Parse current selected event from global variable
  const currentSelectedEvent = useMemo<string | null>(() => {
    if (!globalVariableData?.blobText) {
      return DEFAULT_SELECTED_EVENTS[0];
    }
    try {
      const parsed = JSON.parse(globalVariableData.blobText);
      if (Array.isArray(parsed)) {
        return parsed[0] ?? null;
      }
      return typeof parsed === 'string' ? parsed : DEFAULT_SELECTED_EVENTS[0];
    } catch {
      return DEFAULT_SELECTED_EVENTS[0];
    }
  }, [globalVariableData?.blobText]);

  // Get the effective selected event (local change or current)
  const selectedEvent = hasLocalChanges
    ? localSelectedEvent
    : currentSelectedEvent;

  const actions = {
    updateSelection: (event: string | null) => {
      setLocalSelectedEvent(event);
      setHasLocalChanges(true);
    },

    saveChanges: async () => {
      try {
        const valueToSave = localSelectedEvent ? [localSelectedEvent] : [];
        await updateGlobalVariableMutation.mutateAsync({
          name: 'HIDE_ALLOWED_EVENT_TYPES',
          command: {
            blobText: JSON.stringify(valueToSave)
          }
        });

        setHasLocalChanges(false);
        setLocalSelectedEvent(null);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        throw new Error('Failed to save allowed events configuration');
      }
    },

    discardChanges: () => {
      setLocalSelectedEvent(null);
      setHasLocalChanges(false);
    },

    refresh: () => {
      refetch();
      setLocalSelectedEvent(null);
      setHasLocalChanges(false);
    }
  };

  // Combine loading states and errors
  const isLoading = isLoadingEventTypes || isLoadingGlobalVariable;
  const error = eventTypesError || globalVariableError;

  return {
    selectedEvent,
    availableEvents: availableEventTypes,
    isLoading,
    error,
    hasUnsavedChanges: hasLocalChanges,
    isSaving: updateGlobalVariableMutation.isPending,
    saveError: updateGlobalVariableMutation.error,
    actions
  };
};
